import { useState, type CSSProperties } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { usersAPI } from '../../api/users'
import { deliveriesAPI } from '../../api/deliveries'
import { caissesAPI, type Caisse } from '../../api/caisses'
import DashAvatar from '../../components/ui/DashAvatar'
import DashIcon from '../../components/ui/DashIcon'
import type { Delivery } from '../../types'

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short', year: 'numeric' })

const VEHICLE_LABEL: Record<string, string> = {
  moto: 'Moto', voiture: 'Voiture', camionnette: 'Camionnette', camion: 'Camion',
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'En attente', validated: 'Validé', assigned: 'Assigné',
  picked_up: 'Récupéré', in_transit: 'En route', delivered: 'Livré',
  failed: 'Échoué', cancelled: 'Annulé', unreachable: 'Injoignable',
}
const STATUS_COLOR: Record<string, string> = {
  delivered: '#10B981', in_transit: '#3B82F6', picked_up: '#F59E0B',
  failed: '#EF4444', cancelled: '#9CA3AF', assigned: '#8B5CF6',
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '18px 20px',
      border: '1px solid #F3F4F6', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color ?? '#0D1117', lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: '#9CA3AF' }}>{sub}</div>}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13,
    }}>
      <span style={{ color: '#9CA3AF', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <span style={{ color: '#0D1117', fontWeight: 500 }}>{value}</span>
    </div>
  )
}

function RecentDeliveries({ deliveries }: { deliveries: Delivery[] }) {
  const navigate = useNavigate()
  if (!deliveries.length) {
    return <div style={{ color: '#9CA3AF', fontSize: 13, padding: '20px 0' }}>Aucune livraison récente.</div>
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #F3F4F6' }}>
            {['CODE', 'DESTINATAIRE', 'ADRESSE', 'STATUT', 'COD', 'DATE'].map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#9CA3AF', fontSize: 10, letterSpacing: '0.04em' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {deliveries.map(d => {
            const color = STATUS_COLOR[d.status] ?? '#9CA3AF'
            return (
              <tr
                key={d._id}
                style={{ borderBottom: '1px solid #F9FAFB', cursor: 'pointer' }}
                onClick={() => navigate(`/admin/livraisons/${d._id}`)}
              >
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0D1117', fontFamily: 'monospace' }}>
                  {d.trackingCode}
                </td>
                <td style={{ padding: '10px 12px', color: '#374151' }}>{d.dropoff?.contactName ?? '—'}</td>
                <td style={{ padding: '10px 12px', color: '#6B7280', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.dropoff?.address?.slice(0, 30)}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: color + '1A', color, borderRadius: 20, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}>
                    {STATUS_LABEL[d.status] ?? d.status}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0D1117' }}>
                  {d.price?.base ?? 0} TND
                </td>
                <td style={{ padding: '10px 12px', color: '#9CA3AF' }}>{fmtDate(d.createdAt)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Commission Card ───────────────────────────────────────────────────────────

const COMMISSION_PRESETS = [2, 3, 4, 5, 6, 8, 10]

function CommissionCard({ livreurId, currentFixe }: { livreurId: string; currentFixe: number }) {
  const [value, setValue] = useState(currentFixe)
  const [editing, setEditing] = useState(false)
  const [custom, setCustom] = useState(false)
  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: (v: number) => usersAPI.setCommission(livreurId, v),
    onSuccess: () => {
      toast.success('Commission mise à jour')
      qc.invalidateQueries({ queryKey: ['user', livreurId] })
      qc.invalidateQueries({ queryKey: ['livreurs'] })
      setEditing(false)
      setCustom(false)
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  })

  const handleOpen = () => {
    setValue(currentFixe)
    setCustom(!COMMISSION_PRESETS.includes(currentFixe))
    setEditing(true)
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #F3F4F6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0D1117', margin: 0 }}>
          <DashIcon name="wallet" size={14} style={{ marginRight: 6, verticalAlign: '-2px', color: '#1D9E75' }} />
          Commission livreur
        </h3>
        {!editing && (
          <button className="btn btn-sm btn-ghost" onClick={handleOpen}>
            Modifier
          </button>
        )}
      </div>

      {/* Current value display */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        background: '#F0FDF4', borderRadius: 10, padding: '20px 16px',
        marginBottom: editing ? 16 : 0,
      }}>
        <div style={{ fontSize: 42, fontWeight: 800, color: '#1D9E75', lineHeight: 1 }}>
          {editing ? value : currentFixe}
          <span style={{ fontSize: 18, fontWeight: 600, marginLeft: 4 }}>TND</span>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
          commission fixe par livraison
        </div>
      </div>

      {editing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Preset buttons */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Montants rapides
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {COMMISSION_PRESETS.map(p => (
                <button
                  key={p}
                  onClick={() => { setValue(p); setCustom(false) }}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: '2px solid',
                    borderColor: value === p && !custom ? '#1D9E75' : '#E5E7EB',
                    background: value === p && !custom ? '#F0FDF4' : '#fff',
                    color: value === p && !custom ? '#1D9E75' : '#374151',
                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  {p} TND
                </button>
              ))}
              <button
                onClick={() => setCustom(true)}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: '2px solid',
                  borderColor: custom ? '#1D9E75' : '#E5E7EB',
                  background: custom ? '#F0FDF4' : '#fff',
                  color: custom ? '#1D9E75' : '#374151',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                Personnalisé
              </button>
            </div>
          </div>

          {/* Custom input */}
          {custom && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>
                Montant personnalisé (TND)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={value}
                  onChange={e => setValue(Math.max(0, Number(e.target.value)))}
                  style={{
                    flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 14,
                    border: '2px solid #1D9E75', outline: 'none', fontWeight: 600,
                  }}
                />
                <span style={{ fontWeight: 600, color: '#6B7280' }}>TND</span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-ghost"
              style={{ flex: 1 }}
              onClick={() => { setEditing(false); setCustom(false) }}
            >
              Annuler
            </button>
            <button
              className="btn btn-primary"
              style={{ flex: 1 }}
              onClick={() => mutation.mutate(value)}
              disabled={mutation.isPending || value < 0}
            >
              {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Caisse Journée Card ───────────────────────────────────────────────────────

function fmt(n: number) {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function CaisseStatutBadge({ statut }: { statut: Caisse['statut'] }) {
  const cfg = {
    ouverte:  { bg: '#D1FAE5', color: '#065F46', label: '● Ouverte' },
    cloturee: { bg: '#FEE2E2', color: '#991B1B', label: '◆ Clôturée' },
    payee:    { bg: '#DBEAFE', color: '#1E40AF', label: '✓ Payée' },
  }[statut]
  return (
    <span style={{ background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>
      {cfg.label}
    </span>
  )
}

function CaisseJourneeCard({ livreurId }: { livreurId: string }) {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['caisse-admin', livreurId, 'day'],
    queryFn:  () => caissesAPI.getAllAdmin({ type: 'day', livreur: livreurId, page: 1 }),
    refetchInterval: 60_000,
  })

  const caisse: Caisse | undefined = data?.caisses?.[0]

  const cloturerMut = useMutation({
    mutationFn: () => caissesAPI.cloturerCaisse(caisse!._id),
    onSuccess: () => {
      toast.success('Caisse clôturée')
      qc.invalidateQueries({ queryKey: ['caisse-admin', livreurId] })
    },
    onError: () => toast.error('Erreur lors de la clôture'),
  })

  const row = (label: string, value: string, accent = false) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '9px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13,
    }}>
      <span style={{ color: '#6B7280' }}>{label}</span>
      <b style={{ color: accent ? '#1D9E75' : '#0D1117', fontSize: accent ? 15 : 13 }}>{value}</b>
    </div>
  )

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #F3F4F6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0D1117', margin: 0 }}>
          <DashIcon name="wallet" size={14} style={{ marginRight: 6, verticalAlign: '-2px', color: '#1D9E75' }} />
          Caisse du jour
        </h3>
        {caisse && <CaisseStatutBadge statut={caisse.statut} />}
      </div>

      {isLoading ? (
        <div style={{ color: '#9CA3AF', fontSize: 13 }}>Chargement…</div>
      ) : !caisse ? (
        <div style={{ color: '#9CA3AF', fontSize: 13, padding: '12px 0' }}>
          Aucune caisse ouverte aujourd'hui.
        </div>
      ) : (
        <>
          {row('Colis assignés', String(caisse.totalColisAssignes))}
          {row('Colis livrés', String(caisse.totalColisLivres))}
          {row('Brut COD perçu', `${fmt(caisse.montantBrut)} TND`)}
          {row('Gain livreur', `${fmt(caisse.gainLivreur)} TND`)}
          {row('Commission TariqGo', `${fmt(caisse.commissionTariqGo)} TND`)}
          {row('Net à encaisser', `${fmt(caisse.montantNet)} TND`, true)}

          {caisse.statut === 'ouverte' && (
            <button
              className="btn btn-primary"
              style={{ width: '100%', marginTop: 16 }}
              onClick={() => {
                if (window.confirm('Clôturer la caisse de ce livreur pour aujourd\'hui ?')) {
                  cloturerMut.mutate()
                }
              }}
              disabled={cloturerMut.isPending}
            >
              {cloturerMut.isPending ? 'Clôture en cours…' : 'Clôturer la caisse'}
            </button>
          )}
        </>
      )}
    </div>
  )
}

// ─── Edit Modal ────────────────────────────────────────────────────────────────

interface EditModalProps { isOpen: boolean; onClose: () => void; user: any; onSuccess: () => void }

function EditModal({ isOpen, onClose, user, onSuccess }: EditModalProps) {
  const [form, setForm] = useState({
    name: user.name ?? '',
    phone: user.phone ?? '',
    email: user.email ?? '',
    vehicleType: user.vehicle?.type ?? '',
    vehicleBrand: user.vehicle?.brand ?? '',
    vehicleModel: user.vehicle?.model ?? '',
    vehiclePlate: user.vehicle?.plate ?? '',
    isVerified: user.isVerified ?? false,
  })
  const [loading, setLoading] = useState(false)

  const set = (f: string, v: string | boolean) => setForm(p => ({ ...p, [f]: v }))

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        isVerified: form.isVerified,
        vehicle: form.vehicleType ? {
          type: form.vehicleType,
          brand: form.vehicleBrand.trim() || undefined,
          model: form.vehicleModel.trim() || undefined,
          plate: form.vehiclePlate.trim() || undefined,
        } : undefined,
      }
      if (form.email.trim()) payload.email = form.email.trim()
      await usersAPI.update(user._id, payload)
      toast.success('Profil mis à jour')
      onSuccess()
      onClose()
    } catch {
      toast.error('Erreur lors de la mise à jour')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const inp: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
    border: '1px solid #E5E7EB', outline: 'none', boxSizing: 'border-box',
  }
  const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 480, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0D1117', marginBottom: 24 }}>Modifier le livreur</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lbl}>Nom *</label>
            <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Téléphone *</label>
            <input style={inp} value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Email</label>
            <input style={inp} value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Véhicule</div>
            <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 10 } as CSSProperties}>
              <div>
                <label style={lbl}>Type</label>
                <select style={inp} value={form.vehicleType} onChange={e => set('vehicleType', e.target.value)}>
                  <option value="">—</option>
                  <option value="moto">Moto</option>
                  <option value="voiture">Voiture</option>
                  <option value="camionnette">Camionnette</option>
                  <option value="camion">Camion</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Plaque</label>
                <input style={inp} value={form.vehiclePlate} onChange={e => set('vehiclePlate', e.target.value)} placeholder="123 TN 456" />
              </div>
              <div>
                <label style={lbl}>Marque</label>
                <input style={inp} value={form.vehicleBrand} onChange={e => set('vehicleBrand', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Modèle</label>
                <input style={inp} value={form.vehicleModel} onChange={e => set('vehicleModel', e.target.value)} />
              </div>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.isVerified} onChange={e => set('isVerified', e.target.checked)} />
            Compte vérifié
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button onClick={handleSubmit} disabled={loading} className="btn btn-primary">
            {loading ? 'Enregistrement…' : 'Mettre à jour'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminLivreurDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showEdit, setShowEdit] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersAPI.getById(id!),
    enabled: !!id,
  })

  const { data: deliveriesData } = useQuery({
    queryKey: ['deliveries', 'livreur', id],
    queryFn: () => deliveriesAPI.getAll({ livreur: id, limit: 10 }),
    enabled: !!id,
  })

  const suspendMut = useMutation({
    mutationFn: ({ suspended, reason }: { suspended: boolean; reason?: string }) =>
      usersAPI.setSuspended(id!, suspended, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', id] })
      qc.invalidateQueries({ queryKey: ['livreurs'] })
      toast.success('Statut mis à jour')
    },
    onError: () => toast.error('Erreur'),
  })

  const verifyMut = useMutation({
    mutationFn: () => usersAPI.verifyLivreur(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user', id] })
      qc.invalidateQueries({ queryKey: ['livreurs'] })
      toast.success('Livreur vérifié')
    },
    onError: () => toast.error('Erreur'),
  })

  const handleSuspend = () => {
    if (!u) return
    if (u.isSuspended) {
      suspendMut.mutate({ suspended: false })
    } else {
      const reason = window.prompt('Motif de suspension :')
      if (reason !== null) suspendMut.mutate({ suspended: true, reason })
    }
  }

  if (isLoading) {
    return (
      <div style={{ padding: 40, color: '#9CA3AF', textAlign: 'center' }}>Chargement…</div>
    )
  }

  const u = data?.user
  const stats = data?.stats as any

  if (!u) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <DashIcon name="alert" size={32} style={{ color: '#EF4444' }} />
        <p style={{ color: '#EF4444', marginTop: 12 }}>Livreur introuvable.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/admin/livreurs')}>← Retour</button>
      </div>
    )
  }

  const deliveries: Delivery[] = (deliveriesData as any)?.data ?? []
  const commissionFixe: number = (u as any).commissionFixe ?? 3

  return (
    <>
      {/* Back */}
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => navigate('/admin/livreurs')}
        style={{ marginBottom: 20 }}
      >
        ← Livreurs
      </button>

      {/* Header */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '20px 24px',
        border: '1px solid #F3F4F6', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <DashAvatar name={u.name} size={56} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0D1117', margin: 0 }}>{u.name}</h2>
            {u.isVerified && (
              <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>
                ✓ Vérifié
              </span>
            )}
            {u.isSuspended && (
              <span style={{ background: '#FEE2E2', color: '#991B1B', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>
                Suspendu
              </span>
            )}
            <span style={{
              background: (u as any).isOnline ? '#D1FAE5' : '#F3F4F6',
              color: (u as any).isOnline ? '#065F46' : '#6B7280',
              fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
            }}>
              {(u as any).isOnline ? '● En ligne' : '○ Hors ligne'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#6B7280' }}>
              <DashIcon name="phone" size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />
              {u.phone}
            </span>
            {u.email && (
              <span style={{ fontSize: 13, color: '#6B7280' }}>
                <DashIcon name="mail" size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />
                {u.email}
              </span>
            )}
            <span style={{ fontSize: 13, color: '#6B7280' }}>
              Membre depuis {fmtDate(u.createdAt)}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowEdit(true)}>
            <DashIcon name="edit" size={13} /> Modifier
          </button>
          {!u.isVerified && (
            <button className="btn btn-primary btn-sm" onClick={() => verifyMut.mutate()} disabled={verifyMut.isPending}>
              ✓ Vérifier
            </button>
          )}
          <button
            className="btn btn-sm btn-ghost"
            style={{ color: u.isSuspended ? '#1D9E75' : '#EF4444' }}
            onClick={handleSuspend}
            disabled={suspendMut.isPending}
          >
            {u.isSuspended ? 'Réactiver' : 'Suspendre'}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 20 }}>
        <KpiCard label="Total livraisons" value={stats?.total ?? 0} />
        <KpiCard label="Livrées" value={stats?.delivered ?? 0} color="#10B981" />
        <KpiCard label="En cours" value={stats?.inProgress ?? 0} color="#3B82F6" />
        <KpiCard label="Échecs" value={stats?.failed ?? 0} color="#EF4444" />
        <KpiCard
          label="Gains totaux"
          value={`${(stats?.totalEarnings ?? 0).toFixed(2)} TND`}
          color="#1D9E75"
          sub="livraisons effectuées"
        />
      </div>

      {/* Profile + Commission */}
      <div className="detail-grid" style={{ '--detail-cols': '1fr 320px', gap: 16, marginBottom: 20, alignItems: 'start' } as CSSProperties}>
        {/* Profile card */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #F3F4F6' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0D1117', marginBottom: 12 }}>
            <DashIcon name="users" size={14} style={{ marginRight: 6, verticalAlign: '-2px', color: '#6B7280' }} />
            Profil & Véhicule
          </h3>
          <InfoRow label="Note" value={u.rating ? `★ ${u.rating}` : undefined} />
          <InfoRow label="Livraisons totales" value={String(u.totalDeliveries ?? 0)} />
          <InfoRow label="Véhicule" value={
            (u as any).vehicle?.type
              ? `${VEHICLE_LABEL[(u as any).vehicle.type] ?? (u as any).vehicle.type}${(u as any).vehicle.brand ? ' – ' + (u as any).vehicle.brand : ''}${(u as any).vehicle.model ? ' ' + (u as any).vehicle.model : ''}`
              : null
          } />
          <InfoRow label="Plaque" value={(u as any).vehicle?.plate} />
          <InfoRow label="Inscription" value={fmtDate(u.createdAt)} />
          {u.isSuspended && (
            <InfoRow label="Motif suspension" value={(u as any).suspensionReason || 'Non renseigné'} />
          )}
        </div>

        {/* Commission card */}
        <CommissionCard livreurId={id!} currentFixe={commissionFixe} />
      </div>

      {/* Caisse du jour */}
      <CaisseJourneeCard livreurId={id!} />

      {/* Recent deliveries */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #F3F4F6', marginTop: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0D1117', marginBottom: 16 }}>
          <DashIcon name="package" size={14} style={{ marginRight: 6, verticalAlign: '-2px', color: '#6B7280' }} />
          Livraisons récentes
        </h3>
        <RecentDeliveries deliveries={deliveries} />
      </div>

      {/* Edit modal */}
      {showEdit && (
        <EditModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          user={u}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['user', id] })
            qc.invalidateQueries({ queryKey: ['livreurs'] })
          }}
        />
      )}
    </>
  )
}
